// Poll + refetch-on-foreground for every live `subscribeViaApi` subscription.
//
// Today Supabase Realtime is the primary "something changed, refetch" signal.
// Phase 2 of the Go/Cloud migration (docs/GO_MIGRATION.md) moves the database
// off Supabase, which permanently silences those Realtime events — this module
// is the replacement: a slow background poll while the app is visible, plus an
// immediate refetch when the app returns to the foreground (tab switch, native
// resume). Until the database moves it simply runs alongside Realtime; the
// refetches are idempotent GETs backed by the read cache, so the overlap is
// harmless.
import { refetchAllSubscriptions } from "./apiClient"

// Slow deliberately: the poll is a freshness backstop, not the interactive
// path — the user's own mutations update the UI optimistically, and the
// foreground trigger covers "I just came back to the app".
const POLL_INTERVAL_MS = 60_000

// Foreground refetches are skipped when ANY refetch ran this recently, so a
// visibility flap (quick app switch, lock/unlock) doesn't hammer the API.
const FOREGROUND_MIN_AGE_MS = 10_000

// Starts the poll + foreground listeners; returns the teardown. Safe to call
// once from App.vue's mount — subscriptions register themselves in apiClient,
// so an empty registry makes the poll a no-op rather than an error.
export function startRefetchPolling(): () => void {
  let lastRefetchAt = 0

  const refetchIfDue = (minAgeMs: number) => {
    if (document.visibilityState !== "visible") return
    // Offline: the outbox owns mutations and reads come from the cache; a
    // poll would just burn the timeout on every subscription.
    if (navigator.onLine === false) return
    if (Date.now() - lastRefetchAt < minAgeMs) return
    lastRefetchAt = Date.now()
    refetchAllSubscriptions().catch(() => {})
  }

  const handleForeground = () => refetchIfDue(FOREGROUND_MIN_AGE_MS)

  // The interval keeps running while hidden (cheap no-op via the visibility
  // check) instead of juggling pause/resume state; the foreground listener
  // covers staleness accrued while hidden.
  const timer = window.setInterval(() => refetchIfDue(0), POLL_INTERVAL_MS)
  document.addEventListener("visibilitychange", handleForeground)
  window.addEventListener("focus", handleForeground)
  return () => {
    window.clearInterval(timer)
    document.removeEventListener("visibilitychange", handleForeground)
    window.removeEventListener("focus", handleForeground)
  }
}
